import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import { Snackbar, Alert } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./apiKeys.scss";
import {
  getApiKeys,
  createApiKey,
  revokeApiKey,
  deleteApiKey,
} from "../../Redux/ActionCreators/settingAction";

const ApiKeys = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [form, setForm] = useState({ description: "", expiry: null });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [newKey, setNewKey] = useState(null);

  const apiKeys = useSelector((state) => state.setting.apiKeys || []);
  const { selectedTenant } = useSelector((state) => state.users);

  const apiIntegrationEnabled =
    selectedTenant?.data?.subscription_plan?.configuration?.feature_flags
      ?.api_integration === true;

  useEffect(() => {
    if (apiIntegrationEnabled) {
      fetchKeys();
    }
  }, [apiIntegrationEnabled]);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    await dispatch(getApiKeys());
    setLoading(false);
  }, [dispatch]);

  const handleCreateClick = () => {
    setForm({ description: "", expiry: null });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.description) {
      setSnack({
        open: true,
        message: "Description is required",
        severity: "error",
      });
      return;
    }

    const payload = {
      description: form.description,
      expires_at: form.expiry ? form.expiry.toISOString() : null,
    };

    const result = await dispatch(createApiKey(payload));
    if (result && result.data) {
      setNewKey(result.data);
      setShowModal(false);
      setShowKeyModal(true);
      fetchKeys();
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const handleRevoke = async (id, currentStatus) => {
    if (currentStatus !== "active") return;

    const result = await dispatch(revokeApiKey(id));
    if (result && result.type === "REVOKE_API_KEY") {
      setSnack({ open: true, message: "API Key revoked", severity: "success" });
    } else {
      setSnack({
        open: true,
        message: "Failed to revoke API Key",
        severity: "error",
      });
    }
  };

  const handleDelete = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedDeleteId) {
      const result = await dispatch(deleteApiKey(selectedDeleteId));
      setShowDeleteModal(false);
      if (result && result.type === "DELETE_API_KEY") {
        setSnack({
          open: true,
          message: "API Key deleted",
          severity: "success",
        });
        fetchKeys();
      } else {
        setSnack({
          open: true,
          message: "Failed to delete API Key",
          severity: "error",
        });
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newKey);
    setSnack({
      open: true,
      message: "Copied to clipboard!",
      severity: "success",
    });
  };

  return (
    <div className="job-table-container">
      <div className="top-navigation"></div>
      <div className="top-navigation">
        <div className="createjob-tab">
          <h4>API Keys</h4>
          <button
            className="create-job"
            onClick={handleCreateClick}
            disabled={!apiIntegrationEnabled}
            style={{
              width: "unset",
              ...(!apiIntegrationEnabled && {
                opacity: 0.4,
                cursor: "not-allowed",
                pointerEvents: "none",
              }),
            }}
          >
            Create Key
          </button>
        </div>
      </div>

      <div className="table-content" style={{ background: "#FFFFFF" }}>
        <Table responsive className="job-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Used</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!apiIntegrationEnabled ? (
              <tr>
                <td colSpan="6">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                      padding: "40px 20px",
                      textAlign: "center",
                    }}
                  >
                    <h3
                      style={{
                        color: "#1a1a2e",
                        fontWeight: 700,
                        fontSize: 17,
                        marginBottom: 10,
                      }}
                    >
                      Feature Not Available
                    </h3>
                    <p
                      style={{
                        color: "#6b7280",
                        fontSize: 13,
                        maxWidth: 320,
                        lineHeight: "1.8",
                        marginBottom: 28,
                      }}
                    >
                      This feature is not active with your current plan.
                      <br />
                      Please upgrade to unlock this feature.
                    </p>
                    <button
                      className="save-btn"
                      style={{
                        border: "none",
                        backgroundColor: "#f75891",
                        minWidth: "78px",
                        height: "40px",
                        fontWeight: "bold",
                        fontSize: "14px",
                        lineHeight: "18px",
                        color: "#fff",
                        borderRadius: "4px",
                        padding: "0 12px",
                      }}
                      onClick={() => navigate("/plan")}
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan="6" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : apiKeys.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No API Keys found.
                </td>
              </tr>
            ) : (
              apiKeys.map((key) => (
                <tr key={key.id}>
                  <td>{key.description}</td>
                  <td>{key.key_prefix}...</td>
                  <td>
                    <span className={`status-badge ${key.status}`}>
                      {key.status}
                    </span>
                  </td>
                  <td>{new Date(key.created_at).toLocaleDateString()}</td>
                  <td>
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {key.status === "active" && (
                        <button
                          className="revoke-btn"
                          onClick={() => handleRevoke(key.id, key.status)}
                          title="Revoke Key"
                          style={{ width: "70px" }}
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(key.id)}
                        title="Delete Key"
                        style={{ width: "90px" }}
                      >
                        <i className="fa fa-trash"></i> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* CREATE MODAL */}
      {showModal && (
        <div className="upload-modal automation-modal">
          <div className="modal-content">
            <div className="modal-head">
              <h3>Create API Key</h3>
              <img
                src="/images/close-icon.svg"
                alt="close"
                onClick={() => setShowModal(false)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="modal-input">
              <div className="automation-dropdown">
                <label>Description *</label>
                <input
                  type="text"
                  placeholder="e.g. Production Server"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              <div className="automation-dropdown">
                <label>Expiration (Optional)</label>
                <DatePicker
                  selected={form.expiry}
                  onChange={(date) => setForm({ ...form, expiry: date })}
                  placeholderText="Select expiry date"
                  className="form-control"
                  minDate={new Date()}
                />
              </div>
            </div>
            <div
              className="d-flex justify-content-end"
              style={{ margin: "0 30px 15px 30px" }}
            >
              <button className="upload-btn" onClick={handleSave}>
                Generate Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SHOW KEY MODAL */}
      {showKeyModal && (
        <div className="upload-modal automation-modal">
          <div className="modal-content">
            <div className="modal-head">
              <h3>API Key Generated</h3>
              <img
                src="/images/close-icon.svg"
                alt="close"
                onClick={() => setShowKeyModal(false)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="p-4">
              <Alert severity="warning" className="mb-3">
                <strong>Important:</strong> Copy this key now. You won't be able
                to see it again!
              </Alert>
              <div className="d-flex gap-2 align-items-center mb-4">
                <code
                  className="p-2 bg-light border rounded flex-grow-1"
                  style={{ wordBreak: "break-all" }}
                >
                  {newKey}
                </code>
                <button
                  className="btn btn-outline-secondary"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  className="upload-btn"
                  onClick={() => setShowKeyModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="upload-modal automation-modal">
          <div className="modal-content">
            <div className="modal-head">
              <h3>Delete API Key</h3>
              <img
                src="/images/close-icon.svg"
                alt="close"
                onClick={() => setShowDeleteModal(false)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="p-4">
              <p>
                Are you sure you want to delete this API Key? This action cannot
                be undone.
              </p>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="upload-btn"
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #111928",
                    color: "#111928",
                  }}
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button className="upload-btn" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default ApiKeys;
